import { useState, useEffect } from "react";
import { PaziaButton, PaziaLoader } from "components/atoms";
import { PaziaModal } from "components/molecules";
import { IDropdownOption, PaziaInput } from "components/atoms";
import { PaziaTabs } from "components/atoms";
import { ModalHeader } from "components/templates/Modals/ModalHeader";
import { ModalPadding } from "components/templates/Modals/ModalPadding";
import { PaziaDropdown } from "components/atoms/PaziaDropdown/PaziaDropdown";
import { FormProvider, useForm } from "react-hook-form";
import {
  useCourseDetailPageConfig,
  shopApi,
  CourseDetailPageConfig,
  CourseDetailPageSectionConfig,
  CourseEnrollmentContent,
  CourseTabsContent,
  CourseStickyBarContent,
  LayoutSectionConfig,
} from "api/shop";
import {
  SECTION_TYPES,
  SECTION_TYPE_LABELS,
  COURSE_DETAIL_SECTION_TYPE_OPTIONS,
  SectionType,
} from "consts/sectionTypes";
import { useToaster } from "utils";
import { CourseHeroForm, CourseHeroFormValues } from "../Sections/SectionForms/CourseHeroForm";
import { CourseEnrollmentForm, CourseEnrollmentFormValues } from "../Sections/SectionForms/CourseEnrollmentForm";
import { CourseTabsForm, CourseTabsFormValues } from "../Sections/SectionForms/CourseTabsForm";
import { CourseStickyBarForm, CourseStickyBarFormValues } from "../Sections/SectionForms/CourseStickyBarForm";
import { CourseGroupListForm, CourseGroupListFormValues } from "../Sections/SectionForms/CourseGroupListForm";
import { SimpleWysiwygForm, SimpleWysiwygFormValues } from "../Sections/SectionForms/SimpleWysiwygForm";
import { TwoColumnsWysiwygForm, TwoColumnsWysiwygFormValues } from "../Sections/SectionForms/TwoColumnsWysiwygForm";
import { ImageContentForm, ImageContentFormValues } from "../Sections/SectionForms/ImageContentForm";
import { ImagesCardsForm, ImagesCardsFormValues } from "../Sections/SectionForms/ImagesCardsForm";
import { LogosSectionForm, LogosSectionFormValues } from "../Sections/SectionForms/LogosSectionForm";
import { SimpleBgImageForm, SimpleBgImageFormValues } from "../Sections/SectionForms/SimpleBgImageForm";
import { ClassicHeroForm, ClassicHeroFormValues } from "../Sections/SectionForms/ClassicHeroForm";
import { DynamicSectionForm, DynamicSectionFormValues } from "../Sections/SectionForms/DynamicSectionForm";
import { MainPageHeroForm, MainPageHeroFormValues } from "../Sections/SectionForms/MainPageHeroForm";
import { ProductsSectionForm, ProductsSectionFormValues } from "../Sections/SectionForms/ProductsSectionForm";
import { MapSectionForm, MapSectionFormValues } from "../Sections/SectionForms/MapSectionForm";
import { OpinionsSectionForm, OpinionsSectionFormValues } from "../Sections/SectionForms/OpinionsSectionForm";
import { ContactLocationsForm, ContactLocationsFormValues } from "../Sections/SectionForms/ContactLocationsForm";
import { ContactFormSectionForm, ContactFormSectionFormValues } from "../Sections/SectionForms/ContactFormSectionForm";
import { PeopleSectionForm, PeopleSectionFormValues } from "../Sections/SectionForms/PeopleSectionForm";
import { CardsSectionForm, CardsSectionFormValues } from "../Sections/SectionForms/CardsSectionForm";
import { MobileStickyCtaForm, MobileStickyCtaFormValues } from "../Sections/SectionForms/MobileStickyCtaForm";
import { StaticPdfForm, StaticPdfFormValues } from "../Sections/SectionForms/StaticPdfForm";
import { VideoSectionForm, VideoSectionFormValues } from "../Sections/SectionForms/VideoSectionForm";
import { VideoContentCardsForm, VideoContentCardsFormValues } from "../Sections/SectionForms/VideoContentCardsForm";
import { useLayoutState } from "../Layout/useLayoutState";
import { LayoutEditor } from "../Layout/LayoutEditor";
import { LayoutPreview } from "../Layout/LayoutPreview";

type AnyFormValues =
  | CourseHeroFormValues
  | CourseEnrollmentFormValues
  | CourseTabsFormValues
  | CourseStickyBarFormValues
  | CourseGroupListFormValues
  | SimpleWysiwygFormValues
  | TwoColumnsWysiwygFormValues
  | ImageContentFormValues
  | ImagesCardsFormValues
  | LogosSectionFormValues
  | SimpleBgImageFormValues
  | ClassicHeroFormValues
  | DynamicSectionFormValues
  | MainPageHeroFormValues
  | ProductsSectionFormValues
  | MapSectionFormValues
  | OpinionsSectionFormValues
  | ContactLocationsFormValues
  | ContactFormSectionFormValues
  | PeopleSectionFormValues
  | CardsSectionFormValues
  | StaticPdfFormValues
  | VideoSectionFormValues
  | VideoContentCardsFormValues;

type EditorTab = "editor" | "preview";

export const ShopCourseDetailPageForm = () => {
  const { courseDetailPageConfig, isLoading, invalidate } = useCourseDetailPageConfig();
  const { showSuccess, showError } = useToaster();
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<EditorTab>("editor");

  const layoutState = useLayoutState();

  // Add section modal state
  const [addModalTarget, setAddModalTarget] = useState<{ rowId: string; columnId: string } | null>(null);

  // Edit section modal state
  const [editTarget, setEditTarget] = useState<{
    rowId: string;
    columnId: string;
    sectionIndex: number;
    section: LayoutSectionConfig;
  } | null>(null);

  // Load config from API
  useEffect(() => {
    if (courseDetailPageConfig) {
      layoutState.loadConfig(courseDetailPageConfig);
    }
  }, [courseDetailPageConfig]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const config: CourseDetailPageConfig = layoutState.getConfig();
      await shopApi.updateCourseDetailPageConfig(config);
      showSuccess("Zapisano konfigurację strony kursu");
      invalidate();
    } catch (error) {
      showError("Nie udało się zapisać konfiguracji");
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddSection = (type: string, name: string, content: AnyFormValues) => {
    if (!addModalTarget) return;
    layoutState.addSection(addModalTarget.rowId, addModalTarget.columnId, {
      type,
      name: name || undefined,
      content,
    });
    setAddModalTarget(null);
  };

  const handleEditSection = (content: AnyFormValues) => {
    if (!editTarget) return;
    layoutState.updateSection(editTarget.rowId, editTarget.columnId, editTarget.sectionIndex, content);
    setEditTarget(null);
  };

  const handleOpenEditSection = (rowId: string, columnId: string, sectionIndex: number) => {
    const row = layoutState.rows.find((r) => r.id === rowId);
    const column = row?.columns.find((c) => c.id === columnId);
    const section = column?.sections[sectionIndex];
    if (section) {
      setEditTarget({ rowId, columnId, sectionIndex, section });
    }
  };

  if (isLoading) return <PaziaLoader position="center" />;

  const tabs = [
    { name: "Edytor", active: activeTab === "editor", onClick: () => setActiveTab("editor") },
    { name: "Podgląd", active: activeTab === "preview", onClick: () => setActiveTab("preview") },
  ];

  return (
    <div className="grid gap-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-slate-800 dark:text-slate-200">Układ strony kursu</h3>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={layoutState.resetToDefault}
            className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
          >
            Przywróć domyślne
          </button>
        </div>
      </div>

      <PaziaTabs tabs={tabs} />

      {activeTab === "editor" && (
        <LayoutEditor
          layoutState={layoutState}
          onAddSection={(rowId, columnId) => setAddModalTarget({ rowId, columnId })}
          onEditSection={handleOpenEditSection}
        />
      )}

      {activeTab === "preview" && <LayoutPreview rows={layoutState.rows} />}

      <div className="flex justify-start">
        <PaziaButton type="success" title="Zapisz" disabled={isSaving} onClick={handleSave} />
      </div>

      {/* Add Section Modal */}
      <AddCourseSectionModal
        isOpen={!!addModalTarget}
        onClose={() => setAddModalTarget(null)}
        onAdd={handleAddSection}
      />

      {/* Edit Section Modal */}
      <EditCourseSectionModal
        isOpen={!!editTarget}
        onClose={() => setEditTarget(null)}
        section={editTarget?.section ?? { type: "", content: {} }}
        onSave={handleEditSection}
      />
    </div>
  );
};

// --- Add Section Modal ---

interface AddCourseSectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (type: string, name: string, content: AnyFormValues) => void;
}

const AddCourseSectionModal = ({ isOpen, onClose, onAdd }: AddCourseSectionModalProps) => {
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [sectionName, setSectionName] = useState("");
  const methods = useForm({ defaultValues: { type: "", name: "" } });

  const handleTypeSelect = (type: string, name: string) => {
    setSelectedType(type);
    setSectionName(name);
  };

  const handleSubmit = (content: AnyFormValues) => {
    if (!selectedType) return;
    onAdd(selectedType, sectionName, content);
    handleClose();
  };

  const handleClose = () => {
    setSelectedType(null);
    setSectionName("");
    methods.reset();
    onClose();
  };

  return (
    <PaziaModal id="add-course-section-modal" onCloseRequest={handleClose} isOpen={isOpen} size="full">
      {isOpen && (
        <ModalPadding>
          <ModalHeader>Dodaj sekcję</ModalHeader>
          {!selectedType ? (
            <div className="space-y-4">
              <p className="text-slate-600 dark:text-slate-400">Wybierz typ sekcji:</p>
              <FormProvider {...methods}>
                <div className="grid max-w-md gap-4">
                  <PaziaInput name="name" label="Nazwa sekcji" placeholder="np. Sekcja główna" />
                  <PaziaDropdown
                    name="type"
                    label="Typ sekcji"
                    options={COURSE_DETAIL_SECTION_TYPE_OPTIONS as IDropdownOption[]}
                    required
                  />
                </div>
              </FormProvider>
              <div className="flex justify-end gap-2 pt-4">
                <button
                  type="button"
                  onClick={handleClose}
                  className="rounded-lg border border-slate-300 px-4 py-2 text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
                >
                  Anuluj
                </button>
                <button
                  type="button"
                  onClick={() => handleTypeSelect(methods.getValues("type"), methods.getValues("name"))}
                  disabled={!methods.watch("type")}
                  className="rounded-lg bg-indigo-600 px-4 py-2 text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Dalej
                </button>
              </div>
            </div>
          ) : (
            <SectionFormByType
              type={selectedType}
              onSubmit={handleSubmit}
              onCancel={() => setSelectedType(null)}
            />
          )}
        </ModalPadding>
      )}
    </PaziaModal>
  );
};

// --- Edit Section Modal ---

interface EditCourseSectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  section: LayoutSectionConfig;
  onSave: (content: AnyFormValues) => void;
}

const EditCourseSectionModal = ({ isOpen, onClose, section, onSave }: EditCourseSectionModalProps) => {
  const handleSubmit = (content: AnyFormValues) => {
    onSave(content);
  };

  return (
    <PaziaModal id="edit-course-section-modal" onCloseRequest={onClose} isOpen={isOpen} size="full">
      {isOpen && (
        <ModalPadding>
          <ModalHeader>
            Edytuj sekcję: {SECTION_TYPE_LABELS[section.type as SectionType] ?? section.type}
          </ModalHeader>
          <SectionFormByType
            type={section.type}
            defaultValues={section.content}
            onSubmit={handleSubmit}
            onCancel={onClose}
          />
        </ModalPadding>
      )}
    </PaziaModal>
  );
};

// --- Shared form renderer ---

interface SectionFormByTypeProps {
  type: string;
  defaultValues?: unknown;
  onSubmit: (content: AnyFormValues) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const SectionFormByType = ({ type, defaultValues, onSubmit, onCancel, isLoading }: SectionFormByTypeProps) => {
  switch (type) {
    case SECTION_TYPES.COURSE_HERO:
      return <CourseHeroForm onSubmit={onSubmit} onCancel={onCancel} defaultValues={defaultValues as any} isLoading={isLoading} />;
    case SECTION_TYPES.COURSE_ENROLLMENT:
      return <CourseEnrollmentForm onSubmit={onSubmit} onCancel={onCancel} defaultValues={defaultValues as CourseEnrollmentContent} isLoading={isLoading} />;
    case SECTION_TYPES.COURSE_TABS:
      return <CourseTabsForm onSubmit={onSubmit} onCancel={onCancel} defaultValues={defaultValues as CourseTabsContent} isLoading={isLoading} />;
    case SECTION_TYPES.COURSE_STICKY_BAR:
      return <CourseStickyBarForm onSubmit={onSubmit} onCancel={onCancel} defaultValues={defaultValues as CourseStickyBarContent} isLoading={isLoading} />;
    case SECTION_TYPES.COURSE_GROUP_LIST:
      return <CourseGroupListForm onSubmit={onSubmit} onCancel={onCancel} defaultValues={defaultValues as any} isLoading={isLoading} />;
    case SECTION_TYPES.SIMPLE_WYSIWYG:
      return <SimpleWysiwygForm onSubmit={onSubmit} onCancel={onCancel} defaultValues={defaultValues as any} isLoading={isLoading} />;
    case SECTION_TYPES.TWO_COLUMNS_WYSIWYG:
      return <TwoColumnsWysiwygForm onSubmit={onSubmit} onCancel={onCancel} defaultValues={defaultValues as any} isLoading={isLoading} />;
    case SECTION_TYPES.IMAGE_CONTENT:
      return <ImageContentForm onSubmit={onSubmit} onCancel={onCancel} defaultValues={defaultValues as any} isLoading={isLoading} />;
    case SECTION_TYPES.IMAGES_CARDS:
      return <ImagesCardsForm onSubmit={onSubmit} onCancel={onCancel} defaultValues={defaultValues as any} isLoading={isLoading} />;
    case SECTION_TYPES.LOGOS_SECTION:
      return <LogosSectionForm onSubmit={onSubmit} onCancel={onCancel} defaultValues={defaultValues as any} isLoading={isLoading} />;
    case SECTION_TYPES.SIMPLE_BG_IMAGE:
      return <SimpleBgImageForm onSubmit={onSubmit} onCancel={onCancel} defaultValues={defaultValues as any} isLoading={isLoading} />;
    case SECTION_TYPES.CLASSIC_HERO:
      return <ClassicHeroForm onSubmit={onSubmit} onCancel={onCancel} defaultValues={defaultValues as any} isLoading={isLoading} />;
    case SECTION_TYPES.DYNAMIC_SECTION:
      return <DynamicSectionForm onSubmit={onSubmit} onCancel={onCancel} defaultValues={defaultValues as any} isLoading={isLoading} />;
    case SECTION_TYPES.MAIN_PAGE_HERO:
      return <MainPageHeroForm onSubmit={onSubmit} onCancel={onCancel} defaultValues={defaultValues as any} isLoading={isLoading} />;
    case SECTION_TYPES.PRODUCTS_SECTION:
      return <ProductsSectionForm onSubmit={onSubmit} onCancel={onCancel} defaultValues={defaultValues as any} isLoading={isLoading} />;
    case SECTION_TYPES.MAP_SECTION:
      return <MapSectionForm onSubmit={onSubmit} onCancel={onCancel} defaultValues={defaultValues as any} isLoading={isLoading} />;
    case SECTION_TYPES.OPINIONS_SECTION:
      return <OpinionsSectionForm onSubmit={onSubmit} onCancel={onCancel} defaultValues={defaultValues as any} isLoading={isLoading} />;
    case SECTION_TYPES.CONTACT_LOCATIONS:
      return <ContactLocationsForm onSubmit={onSubmit} onCancel={onCancel} defaultValues={defaultValues as any} isLoading={isLoading} />;
    case SECTION_TYPES.CONTACT_FORM:
      return <ContactFormSectionForm onSubmit={onSubmit} onCancel={onCancel} defaultValues={defaultValues as any} isLoading={isLoading} />;
    case SECTION_TYPES.PEOPLE_SECTION:
      return <PeopleSectionForm onSubmit={onSubmit} onCancel={onCancel} defaultValues={defaultValues as any} isLoading={isLoading} />;
    case SECTION_TYPES.CARDS_SECTION:
      return <CardsSectionForm onSubmit={onSubmit} onCancel={onCancel} defaultValues={defaultValues as any} isLoading={isLoading} />;
    case SECTION_TYPES.MOBILE_STICKY_CTA:
      return <MobileStickyCtaForm onSubmit={onSubmit} onCancel={onCancel} defaultValues={defaultValues as any} isLoading={isLoading} />;
    case SECTION_TYPES.STATIC_PDF:
      return <StaticPdfForm onSubmit={onSubmit} onCancel={onCancel} defaultValues={defaultValues as any} isLoading={isLoading} />;
    case SECTION_TYPES.VIDEO_SECTION:
      return <VideoSectionForm onSubmit={onSubmit} onCancel={onCancel} defaultValues={defaultValues as any} isLoading={isLoading} />;
    case SECTION_TYPES.VIDEO_CONTENT_CARDS:
      return <VideoContentCardsForm onSubmit={onSubmit} onCancel={onCancel} defaultValues={defaultValues as any} isLoading={isLoading} />;
    default:
      return <p className="text-sm text-slate-500">Nieznany typ sekcji: {type}</p>;
  }
};
